import styled, { css } from 'styled-components';
import { forwardRef, useMemo, type CSSProperties, type ReactNode } from 'react';
import type { Breakpoint, CompiledCell } from '@packages/matrix-solver';
import { rem } from '@packages/ui/tokens';
import { palette } from '@packages/ui/tokens';
import { Lozenge } from '@packages/ui/primitives';

export const MATRIX_FIELD_COLUMN_GAP = '16px';
export const MATRIX_FIELD_ROW_GAP = '18px';

function buildItemPlacementCSS(cells: Partial<Record<Breakpoint, CompiledCell>>): string {
  const cell = cells.base;
  if (!cell) return '';

  let css = `grid-column: ${cell.gridColumnStart} / span ${cell.gridSpan}; grid-row: ${cell.gridRowStart};`;

  if (cell.minWidthPx != null || cell.maxWidthPx != null) {
    css += ' width: 100%; margin-right: auto;';
    if (cell.minWidthPx != null) css += ` min-width: ${cell.minWidthPx}px;`;
    if (cell.maxWidthPx != null) css += ` max-width: ${cell.maxWidthPx}px;`;
  }

  return css;
}

const MatrixFieldsRootBase = styled.div`
  box-sizing: border-box;
  display: grid;
  width: 100%;
  column-gap: ${MATRIX_FIELD_COLUMN_GAP};
  row-gap: var(--matrix-row-gap, ${MATRIX_FIELD_ROW_GAP});
  grid-auto-rows: auto;
  align-items: start;

  /*
   * Section-lead spacing: breathe from the prior sibling block (fields / each / udf).
   * Skip when this grid is the first child — otherwise the first section creates a
   * dead 32px gap under the page title or card chrome (e.g. Participant #1 badge).
   */
  &[data-section-lead]:not(:first-child) {
    margin-top: ${rem(32)};
  }

  &[data-standalone-header] {
    margin-bottom: ${rem(6)};
  }

  &[data-standalone-header]:not(:first-child) {
    margin-top: ${rem(32)};
  }
`;

export const MatrixFieldsRoot = forwardRef<
  HTMLDivElement,
  {
    columnCount: number;
    children?: ReactNode;
    style?: CSSProperties;
    /** Block starts with a section-divider — apply lead margin when not first sibling. */
    sectionLead?: boolean;
    /** Divider-only header block (e.g. each-node title) — also keeps bottom clearance. */
    standaloneHeader?: boolean;
  }
>(function MatrixFieldsRoot(
  { columnCount, children, style: extraStyle, sectionLead, standaloneHeader },
  ref,
) {
  const style = useMemo<CSSProperties>(
    () => ({ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, ...extraStyle }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnCount, extraStyle],
  );
  return (
    <MatrixFieldsRootBase
      ref={ref}
      style={style}
      data-section-lead={sectionLead || undefined}
      data-standalone-header={standaloneHeader || undefined}
    >
      {children}
    </MatrixFieldsRootBase>
  );
});

const TRANSIENT = ['$placementCSS', '$isVirtual'] as const;
const shouldForward = (prop: string | number): boolean =>
  !TRANSIENT.includes(prop as (typeof TRANSIENT)[number]);

export const MatrixFieldShell = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $placementCSS: string;
  $isVirtual: boolean;
}>`
  box-sizing: border-box;
  min-width: 0;

  ${({ $placementCSS }) => $placementCSS}

  ${({ $isVirtual }) =>
    $isVirtual &&
    css`
      visibility: hidden;
      pointer-events: none;
      height: 0;
      min-height: 0;
      margin: 0;
      padding: 0;
      overflow: hidden;
      opacity: 0;
    `}
`;

export function compileCellPlacement(
  cellMap: Record<string, Partial<Record<Breakpoint, CompiledCell>>>,
  id: string
): string {
  return buildItemPlacementCSS(cellMap[id] ?? {});
}

/**
 * Section divider spacing rationale (legacy parity):
 *
 * Legacy `.employeeAddress` used `padding-bottom: 12px` on the title, with
 * no row-gap in the Tailwind grid. In our CSS grid the `row-gap` (18px) is
 * always added between the divider row and the first field row. To achieve the
 * same ~12px visual gap we use a negative `margin-bottom` on the root to
 * counteract the row-gap:
 *
 *   row-gap(18) - margin-bottom(-6) = 12px net ≈ legacy 12px ✓
 *
 * For non-first sections the space above in legacy was just implicit grid-row
 * height from the prior fields. We match it with `margin-top: rem(16)` which
 * reads as: prior-field-row bottom + 18px row-gap + 16px = natural rhythm.
 *
 * When `$hasDivider` is true (explicit top border) we add a small padding-top
 * for breathing room before the border.
 */
const SectionDividerRoot = styled.div<{
  $placementCSS: string;
  $hasDivider: boolean;
  $isFirst: boolean;
  $isLozenge: boolean;
}>`
  box-sizing: border-box;
  min-width: 0;
  margin-top: ${({ $isFirst }) => ($isFirst ? '0' : rem(20))};
  /* For lozenge badges the negative margin-bottom would pull the following fields
     too close — lozenges have their own visual weight and need positive clearance. */
  margin-bottom: ${({ $isLozenge }) => ($isLozenge ? rem(4) : rem(-6))};
  ${({ $placementCSS }) => $placementCSS}

  ${({ $hasDivider }) =>
    $hasDivider &&
    css`
      padding-top: ${rem(8)};
      border-top: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
    `}
`;

const DividerHeading = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(20)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: #595959;
  margin-bottom: 0;
`;

const DividerLabel = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${rem(4)};
`;

const DividerSubtitle = styled.p`
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  margin: ${rem(-8)} 0 ${rem(16)} 0;
`;

export interface MatrixSectionDividerProps {
  /** Section heading. When absent the component renders as a silent row-break (no visible chrome). */
  title?: string;
  subtitle?: string;
  titleVariant?: 'label' | 'heading';
  divider?: boolean;
  semanticId?: string;
  placementCSS: string;
  /** True when this is the first section divider in the layout — suppresses top margin. */
  isFirst?: boolean;
  /** Render title as a Lozenge badge instead of plain text. */
  titleStyle?: 'text' | 'lozenge';
  /** Lozenge variant when `titleStyle` is `'lozenge'`. */
  lozengeVariant?: string;
}

export function MatrixSectionDivider({
  title,
  subtitle,
  titleVariant = 'label',
  divider = true,
  semanticId,
  placementCSS,
  isFirst = false,
  titleStyle = 'text',
  lozengeVariant = 'success',
}: MatrixSectionDividerProps): React.ReactElement {
  if (!title) {
    return <div style={{ gridColumn: '1 / -1', height: 0 }} aria-hidden='true' />;
  }

  const TitleComponent = titleVariant === 'heading' ? DividerHeading : DividerLabel;

  return (
    <SectionDividerRoot
      $placementCSS={placementCSS}
      $hasDivider={divider}
      $isFirst={isFirst}
      $isLozenge={titleStyle === 'lozenge'}
      data-semantic-id={semanticId}>
      {titleStyle === 'lozenge' ? (
        <Lozenge variant={lozengeVariant as 'success' | 'info' | 'warning' | 'default'} title={title} />
      ) : (
        <TitleComponent>{title}</TitleComponent>
      )}
      {subtitle != null && subtitle !== '' && <DividerSubtitle>{subtitle}</DividerSubtitle>}
    </SectionDividerRoot>
  );
}
